"use client";

import { useState } from "react";

import { CcConversionRange } from "@/components/conversion/CcConversionRange";
import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import type { BookCallPopupVariant } from "@/lib/book-call-popup-copy";
import { formatCopy } from "@/lib/copy-format";
import { devSectionAttr } from "@/lib/dev/section-attr";

import type { CampaignCalculatorCopy } from "./campaign-landing-page-copy";

/** Working weeks and hours per day behind the yearly figure (stated in `copy.note`). */
const WEEKS_PER_YEAR = 46;
const HOURS_PER_DAY = 8;

type CcCampaignCalculatorProps = {
  copy: CampaignCalculatorCopy;
  bookCallVariant: BookCallPopupVariant;
};

/**
 * A low-commitment step for visitors not ready to book: what the manual work
 * costs them per year, from their own numbers. It promises no saving; the
 * meeting is where that gets estimated, which is what the button offers.
 */
export function CcCampaignCalculator({ copy, bookCallVariant }: CcCampaignCalculatorProps) {
  const [people, setPeople] = useState(2);
  const [hours, setHours] = useState(6);
  const [rate, setRate] = useState(450);
  const number = new Intl.NumberFormat("sv-SE");
  const yearlyHours = people * hours * WEEKS_PER_YEAR;
  const yearlyCost = yearlyHours * rate;
  const days = Math.round(yearlyHours / HOURS_PER_DAY);

  return (
    <section
      className="cc-section cc-campaign-calc"
      aria-labelledby="cc-campaign-calc-title"
      data-cc-nav="light"
      data-campaign-calculator
      {...devSectionAttr("campaign-calculator")}
    >
      <div className="cc-campaign-calc__card">
        <div className="cc-campaign-calc__inputs">
          <CcSanjayaEyebrow theme="on-subtle">{copy.eyebrow}</CcSanjayaEyebrow>
          <h2 id="cc-campaign-calc-title" className="cc-campaign-calc__title">
            {copy.title}
          </h2>
          <p className="cc-campaign-calc__intro">{copy.intro}</p>
          <div className="cc-convert__ranges cc-campaign-calc__ranges">
            <CcConversionRange
              label={copy.people.label}
              value={people}
              min={1}
              max={20}
              step={1}
              display={`${people} ${copy.people.unit}`}
              onChange={setPeople}
            />
            <CcConversionRange
              label={copy.hours.label}
              value={hours}
              min={1}
              max={30}
              step={1}
              display={`${hours} ${copy.hours.unit}`}
              onChange={setHours}
            />
            <CcConversionRange
              label={copy.rate.label}
              value={rate}
              min={250}
              max={1000}
              step={25}
              display={`${number.format(rate)} ${copy.rate.unit}`}
              onChange={setRate}
            />
          </div>
        </div>
        <div className="cc-campaign-calc__result" aria-live="polite">
          <p className="cc-campaign-calc__result-label">{copy.resultLabel}</p>
          <p className="cc-campaign-calc__result-value">
            {number.format(yearlyCost)} <span>{copy.resultUnit}</span>
          </p>
          <p className="cc-campaign-calc__days">{formatCopy(copy.days, { days: number.format(days) })}</p>
          <span className="cc-campaign-cta-hook" data-campaign-cta="calculator">
            <CcButton bookCallPopup bookCallVariant={bookCallVariant} label={copy.cta} expand expandCompact />
          </span>
          <p className="cc-campaign-calc__note">{copy.note}</p>
        </div>
      </div>
    </section>
  );
}
